import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import { apiClient } from './client';
import type { AuthResponse, User } from '../types';

// When the account has a registered passkey/security key, /auth/login returns
// this challenge instead of a session — the second factor must be completed
// (see webauthnApi.authenticate) before a session is issued.
export interface MfaChallenge {
  mfaRequired: true;
  mfaToken: string;
  authenticationOptions: PublicKeyCredentialRequestOptionsJSON;
}

export type LoginResult = AuthResponse | MfaChallenge;

// Clickwrap consent recorded with a brand-new account (password or OAuth signup).
export interface ConsentFields {
  acceptedTerms: boolean;
  termsVersion: string;
  ageConfirmed: boolean;
}

export interface RegisterPayload extends ConsentFields {
  email: string;
  password: string;
  fullName?: string;
  captchaToken?: string;
}

// Public self-service registration returns a user when the API auto-logs the
// new account in (201), or just a message on the silent-duplicate path (200).
export type RegisterResponse =
  | AuthResponse
  | { message: string };

export const authApi = {
  async login(
    email: string,
    password: string,
    rememberMe: boolean = false,
    captchaToken?: string,
  ): Promise<LoginResult> {
    const response = await apiClient.post<LoginResult>('/auth/login', {
      email,
      password,
      rememberMe,
      captchaToken,
    });
    return response.data;
  },

  async register(payload: RegisterPayload): Promise<RegisterResponse> {
    const response = await apiClient.post<RegisterResponse>(
      '/auth/register',
      payload,
    );
    return response.data;
  },

  async requestMagicLink(email: string, captchaToken?: string): Promise<void> {
    await apiClient.post('/auth/magic-link/request', { email, captchaToken });
  },

  async consumeMagicLink(token: string): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/magic-link/consume', { token });
    return response.data;
  },

  async oauthCallback(
    provider: 'google',
    token: string,
    consent?: ConsentFields,
  ): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>(
      `/auth/oauth/${provider}/callback`,
      { token, ...consent },
    );
    return response.data;
  },

  // Creates the invited account and joins the household, but does NOT establish
  // a session — the API emails an "account is ready" welcome and the invitee
  // logs in themselves (mirrors the admin-invite / account-setup flow).
  async inviteSignup(token: string, password: string, fullName?: string): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/invite-signup', {
      token,
      password,
      fullName,
    });
    return response.data;
  },

  async logout(): Promise<void> {
    await apiClient.post('/auth/logout');
  },

  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<User>('/auth/me');
    return response.data;
  },

  async requestPasswordReset(email: string, captchaToken?: string): Promise<void> {
    await apiClient.post('/auth/password-reset/request', { email, captchaToken });
  },

  async resetPassword(token: string, password: string): Promise<void> {
    await apiClient.post('/auth/password-reset/confirm', { token, password });
  },

  // Completes an admin invite: sets the invitee's first password using the
  // setup token from their invite email. Distinct from resetPassword so the
  // API sends the "account is ready" welcome rather than a reset notice.
  async setupAccount(token: string, password: string): Promise<void> {
    await apiClient.post('/auth/account-setup', { token, password });
  },

  // Read-only check used by the reset page on load to decide whether to show
  // the form or a recovery view. Does not consume the token.
  async validateResetToken(token: string): Promise<boolean> {
    const { data } = await apiClient.get<{ valid: boolean }>(
      '/auth/password-reset/validate',
      { params: { token } },
    );
    return data.valid;
  },

  async verifyEmail(token: string): Promise<void> {
    await apiClient.post('/auth/verify-email', { token });
  },

  async resendVerification(email: string, captchaToken?: string): Promise<void> {
    await apiClient.post('/auth/resend-verification', { email, captchaToken });
  },

  // Change the signed-in user's password. The API keeps the current session
  // alive (re-issues cookies) while revoking every other session, so the caller
  // must NOT log the user out afterwards.
  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>(
      '/auth/change-password',
      { currentPassword, newPassword },
    );
    return response.data;
  },

  // Request an email change. The API only emails a confirmation link to the new
  // address — the user's sign-in email does NOT change until they click it.
  async changeEmail(
    newEmail: string,
    password: string,
  ): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>(
      '/auth/change-email',
      { newEmail, password },
    );
    return response.data;
  },

  // Permanently delete the signed-in user's account and data. Axios DELETE
  // bodies travel under `config.data`. The caller must clear the session
  // (logout) afterwards since the account no longer exists.
  async deleteAccount(password: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>('/auth/account', {
      data: { password },
    });
    return response.data;
  },
};
